import { useState, useEffect } from "react";
import { useColors } from "../ColorsContext";
import ProgressDots from "../components/ProgressDots";
import WristbandCTA from "../components/WristbandCTA";

const MAX_TRIES = 3;

export default function ScreenCheckpointPuzzle({
  path, checkpoint, checkpointIndex, totalCheckpoints, onComplete, onBack, layout,
}) {
  const C = useColors();
  const { scale = 1, offsetX = 0, vpWidth = 390 } = layout || {};
  const extraLeft = offsetX / scale;
  const edgeStyle = {
    marginLeft: -extraLeft, width: vpWidth / scale,
    paddingLeft: extraLeft + 14, paddingRight: extraLeft + 14,
  };
  const [answer, setAnswer] = useState("");
  const [showHint, setShowHint] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [correct, setCorrect] = useState(false);
  const [tries, setTries] = useState(MAX_TRIES);
  const [wrongMsg, setWrongMsg] = useState("");
  const [shake, setShake] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [mode, setMode] = useState("physical");
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  const outOfTries = tries === 0 && !correct;
  const canScan = correct || outOfTries;

  function computeXP() {
    if (outOfTries) return 0;
    const triesUsed = MAX_TRIES - tries;
    return Math.max(0, Math.round(checkpoint.xp * (1 - 0.2 * triesUsed) * (hintUsed ? 0.8 : 1)));
  }

  function handleSubmit() {
    if (correct || outOfTries) return;
    const isCorrect = answer.trim().toLowerCase() === checkpoint.answer.toLowerCase();
    if (isCorrect) {
      setCorrect(true);
      setWrongMsg("");
    } else {
      const newTries = tries - 1;
      setTries(newTries);
      setShake(true);
      setWrongMsg(newTries === 0 ? "" : `✗ Incorrect — ${newTries} attempt${newTries !== 1 ? "s" : ""} left`);
    }
  }

  useEffect(() => {
    if (!shake) return;
    const t = setTimeout(() => setShake(false), 400);
    return () => clearTimeout(t);
  }, [shake]);

  function handleScan() {
    if (!canScan) return;
    setScanning(true);
    setTimeout(() => onComplete({ xp: computeXP() }), 1200);
  }

  const displayXP = computeXP();

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: C.navy, position: "relative" }}>

      {/* Top bar */}
      <div style={{
        background: C.navyMid, paddingTop: 10, paddingBottom: 10,
        display: "flex", alignItems: "center", gap: 8,
        borderBottom: `1px solid ${C.dim}`, flexShrink: 0,
        ...edgeStyle,
      }}>
        <button
          onClick={() => setShowLeaveModal(true)}
          style={{
            background: "transparent", border: "none",
            color: C.muted, fontSize: 20, cursor: "pointer",
            padding: "4px 6px", lineHeight: 1, flexShrink: 0,
          }}
        >←</button>
        <div style={{ fontSize: 11, color: path.color, fontWeight: 700, letterSpacing: 1, flex: 1 }}>
          {path.label.toUpperCase()} · CP {checkpointIndex + 1}/{totalCheckpoints}
        </div>
        <ProgressDots total={totalCheckpoints} current={checkpointIndex} />
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: "auto", padding: 16 }}>

        {/* Participation toggle */}
        <div style={{
          display: "flex", gap: 6, marginBottom: 12,
          background: C.card, borderRadius: 8, padding: 4,
          border: `1px solid ${C.dim}`,
        }}>
          {[["physical", "🏃 Physical"], ["alternative", "♿ Alternative"]].map(([m, label]) => (
            <button key={m} onClick={() => setMode(m)} style={{
              flex: 1, padding: "8px 0", borderRadius: 6, border: "none",
              background: mode === m ? path.color : "transparent",
              color: mode === m ? "#fff" : C.muted,
              fontWeight: 600, fontSize: 12, cursor: "pointer", transition: "all 0.2s",
            }}>{label}</button>
          ))}
        </div>

        {/* Obstacle header */}
        <div style={{
          background: C.card, borderRadius: 10, padding: "12px 14px",
          border: `1px solid ${C.dim}`, marginBottom: 12,
          display: "flex", gap: 12, alignItems: "center",
        }}>
          <div style={{
            width: 38, height: 38, borderRadius: 8, flexShrink: 0,
            background: `${path.color}22`, border: `1px solid ${path.color}44`,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
          }}>🧗</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: C.white, fontSize: 14 }}>{checkpoint.obstacle}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{checkpoint.type}</div>
          </div>
          <div style={{
            background: `${outOfTries ? C.red : path.color}22`,
            color: outOfTries ? C.red : path.color,
            borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 700, flexShrink: 0,
          }}>+{displayXP} XP</div>
        </div>

        {/* Story context — compact */}
        <div style={{
          fontSize: 12, color: C.muted, fontStyle: "italic",
          marginBottom: 12, padding: "8px 12px",
          background: C.card, borderRadius: 8,
          borderLeft: `3px solid ${path.color}`,
          lineHeight: 1.5,
        }}>
          {checkpoint.story[path.id]}
        </div>

        {mode === "alternative" && (
          <div style={{
            background: `${C.gold}15`, borderRadius: 8, padding: 10,
            border: `1px solid ${C.gold}44`, marginBottom: 12,
            fontSize: 12, color: C.gold, lineHeight: 1.5,
          }}>
            <strong>Alternative Role:</strong> While your teammate completes the physical obstacle,
            you solve the clue below. Both of you must finish for the team to advance.
            🔊 Audio description available on request.
          </div>
        )}

        {/* ── MISSION CLUE — visual hero ── */}
        <div style={{ marginBottom: 14 }}>
          <div style={{
            fontSize: 13, color: path.color, fontWeight: 800,
            letterSpacing: 1.5, marginBottom: 8,
          }}>MISSION CLUE</div>
          <div style={{
            background: `${path.color}12`, borderRadius: 10, padding: "16px 18px",
            border: `3px solid ${path.color}55`,
            borderLeft: `5px solid ${path.color}`,
            fontSize: 15, color: C.light, lineHeight: 1.75,
          }}>
            "{checkpoint.clue}"
          </div>
        </div>

        {/* Out of tries */}
        {outOfTries && (
          <div style={{
            background: `${C.red}18`, borderRadius: 10, padding: 14,
            border: `2px solid ${C.red}55`, marginBottom: 12, textAlign: "center",
          }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>⚠️</div>
            <div style={{ fontWeight: 800, color: C.red, fontSize: 14, marginBottom: 6 }}>
              🔊 INTEL COMPROMISED
            </div>
            <div style={{ fontSize: 13, color: C.light }}>
              The answer was <strong style={{ color: C.red }}>"{checkpoint.answer}"</strong>.
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>0 XP earned. Scan to continue.</div>
          </div>
        )}

        {/* Correct result */}
        {correct && (
          <div style={{
            background: `${C.teal}18`, borderRadius: 10, padding: 14,
            border: `1px solid ${C.teal}55`, marginBottom: 12, textAlign: "center",
          }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>
            <div style={{ fontWeight: 800, color: C.teal, fontSize: 14, marginBottom: 6 }}>
              🔊 Correct! Checkpoint complete.
            </div>
            <div style={{ fontSize: 13, color: C.light }}>
              +{displayXP} XP earned.
              {displayXP < checkpoint.xp && <span style={{ color: C.muted }}> (penalty applied)</span>}
            </div>
          </div>
        )}

        {/* Input area — only when still active */}
        {!correct && !outOfTries && (
          <>
            {/* Attempt pips */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
              <span style={{ fontSize: 10, color: C.muted, letterSpacing: 1 }}>ATTEMPTS</span>
              {[0, 1, 2].map(i => {
                const used = i >= tries;
                return (
                  <div key={i} style={{
                    width: 14, height: 14, borderRadius: "50%",
                    background: used ? "transparent" : path.color,
                    border: `2px solid ${used ? C.red : path.color}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 8, color: C.red, transition: "all 0.3s",
                  }}>{used ? "✕" : ""}</div>
                );
              })}
              <span style={{ fontSize: 10, color: C.muted }}>{tries} remaining</span>
            </div>

            {/* Answer input */}
            <div style={{ marginBottom: 10 }}>
              <input
                value={answer}
                onChange={e => { setAnswer(e.target.value); setWrongMsg(""); }}
                onKeyDown={e => e.key === "Enter" && answer && handleSubmit()}
                placeholder="Type your answer..."
                aria-label="Enter your answer to the clue"
                style={{
                  width: "100%", padding: "13px 16px", borderRadius: 8,
                  background: C.card,
                  border: `1px solid ${shake ? C.red : C.dim}`,
                  color: C.white, fontSize: 14, outline: "none",
                  boxSizing: "border-box", fontFamily: "inherit", minHeight: 48,
                  transform: shake ? "translateX(-6px)" : "translateX(0)",
                  transition: shake ? "transform 0.1s" : "transform 0.15s, border-color 0.2s",
                }}
                onFocus={e => (e.currentTarget.style.borderColor = path.color)}
                onBlur={e => (e.currentTarget.style.borderColor = C.dim)}
              />
              {wrongMsg && (
                <div role="alert" style={{ fontSize: 12, color: C.red, marginTop: 6, paddingLeft: 4, fontWeight: 600 }}>
                  {wrongMsg}
                </div>
              )}
            </div>

            {/* Hint button — full-width, prominent gold */}
            <button
              onClick={() => { setShowHint(true); setHintUsed(true); }}
              style={{
                width: "100%", padding: "12px 0", marginBottom: 8,
                background: `${C.gold}22`,
                border: `1.5px solid ${hintUsed ? C.gold : C.gold + "88"}`,
                color: C.gold, borderRadius: 8,
                fontSize: 13, fontWeight: 700, cursor: "pointer", minHeight: 48,
              }}
            >
              💡 {hintUsed ? "Hint Used (−20% XP)" : "Use a Hint"}
            </button>

            {/* Submit button — full-width */}
            <button
              onClick={handleSubmit}
              disabled={!answer}
              style={{
                width: "100%", padding: "12px 0", marginBottom: 12,
                background: answer ? C.pink : C.dim,
                color: answer ? "#fff" : C.muted,
                border: "none", borderRadius: 8, fontWeight: 700,
                fontSize: 13, cursor: answer ? "pointer" : "not-allowed", minHeight: 48,
              }}
            >SUBMIT →</button>

            {/* Hint reveal */}
            {showHint && (
              <div style={{
                background: `${C.gold}15`, borderRadius: 8, padding: 14,
                border: `1px solid ${C.gold}44`, marginBottom: 12,
              }}>
                <div style={{ fontSize: 11, color: C.gold, fontWeight: 700, marginBottom: 6 }}>💡 HINT 🔊</div>
                <div style={{ fontSize: 13, color: C.light, lineHeight: 1.5 }}>{checkpoint.hint}</div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Sticky wristband CTA */}
      <WristbandCTA
        canScan={canScan}
        outOfTries={outOfTries}
        pathColor={path.color}
        scanning={scanning}
        onScan={handleScan}
        layout={layout}
      />

      {/* Leave confirmation modal */}
      {showLeaveModal && (
        <div style={{
          position: "absolute", inset: 0,
          background: "rgba(0,0,0,0.72)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 100, padding: 24,
        }}>
          <div style={{
            background: C.card, borderRadius: 14, padding: 24,
            border: `1px solid ${C.dim}`, width: "100%",
          }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.white, marginBottom: 8 }}>
              Leave this checkpoint?
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
              Your progress on this challenge will be lost.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setShowLeaveModal(false)}
                style={{
                  flex: 1, padding: "12px 0", background: "transparent",
                  border: `1px solid ${C.dim}`, borderRadius: 8,
                  color: C.muted, fontSize: 13, cursor: "pointer",
                }}
              >Cancel</button>
              <button
                onClick={() => { setShowLeaveModal(false); onBack(); }}
                style={{
                  flex: 1, padding: "12px 0", background: C.red,
                  border: "none", borderRadius: 8,
                  color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >Leave</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
